import { useEffect, useState } from "react";
import { api, formatApiErrorDetail, API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ExportMenu } from "@/components/ExportMenu";
import { Plus, Upload, DownloadSimple, ClockClockwise, ShieldCheck, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { ArchiveButton } from "@/components/ArchiveButton";

const EMPTY = { title: "", permit_type: "", issuer: "", reference_no: "", issue_date: "", expiry_date: "", notes: "" };

export default function PermitsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [versionsOf, setVersionsOf] = useState(null);
  const canManage = can(user, ["admin", "finance", "procurement"]);

  async function load() {
    const { data } = await api.get("/permits");
    setItems(data);
  }
  useEffect(() => { load(); }, []);

  async function submit(e) {
    e.preventDefault();
    try {
      if (editing) { await api.put(`/permits/${editing.id}`, form); toast.success("Permit updated"); }
      else { await api.post("/permits", form); toast.success("Permit created"); }
      setOpen(false); setEditing(null); setForm(EMPTY); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed"); }
  }

  async function uploadVersion(pid, file, notes) {
    const fd = new FormData();
    fd.append("file", file);
    if (notes) fd.append("notes", notes);
    const token = localStorage.getItem("access_token");
    try {
      const res = await fetch(`${API_BASE}/permits/${pid}/versions`, {
        method: "POST", body: fd, headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      toast.success("New version uploaded");
      load();
    } catch { toast.error("Upload failed"); }
  }

  return (
    <div className="space-y-6" data-testid="permits-page">
      <div className="flex items-end justify-between border-b border-border pb-6">
        <div>
          <div className="section-caption mb-2">Module 07</div>
          <h1 className="section-title text-3xl sm:text-4xl">Permit Repository</h1>
          <p className="text-sm text-muted-foreground mt-1">Central store of permits and compliance docs with version history.</p>
        </div>
        {canManage && <Button onClick={() => { setEditing(null); setForm(EMPTY); setOpen(true); }} data-testid="new-permit-btn"><Plus size={16} className="mr-2" /> New Permit</Button>}
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-border rounded-md p-10 text-center text-sm text-muted-foreground">No permits yet. Create one to start uploading versions.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map((p) => (
            <div key={p.id} className="border border-border rounded-md p-5 bg-card flex flex-col" data-testid={`permit-card-${p.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{p.permit_type || "Permit"}</div>
                  <div className="font-heading font-bold text-lg leading-tight tracking-tight truncate">{p.title}</div>
                  <div className="text-xs text-muted-foreground truncate">{p.issuer || "—"} · {p.reference_no || "—"}</div>
                </div>
                <div className="border border-border rounded-md px-2 py-1 text-[10px] font-medium uppercase tracking-widest bg-foreground text-background">
                  v{p.latest_version || 0}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs mb-4">
                <div className="border border-border rounded px-2 py-1.5">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Issue</div>
                  <div>{p.issue_date || "—"}</div>
                </div>
                <div className="border border-border rounded px-2 py-1.5">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Expiry</div>
                  <div>{p.expiry_date || "—"}</div>
                </div>
              </div>

              {p.latest_file_url ? (
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}${p.latest_file_url}${p.latest_file_url.startsWith("/api/files/") ? `?auth=${encodeURIComponent(localStorage.getItem("access_token") || "")}` : ""}`}
                  target="_blank" rel="noreferrer"
                  className="flex items-center gap-2 text-xs border border-border rounded-md px-3 py-2 hover:bg-muted mb-3"
                  data-testid={`permit-download-${p.id}`}
                >
                  <DownloadSimple size={14} /> {p.latest_file_name || "Download latest"}
                </a>
              ) : (
                <div className="text-xs text-muted-foreground border border-dashed border-border rounded-md p-3 mb-3">No files uploaded yet.</div>
              )}

              <div className="flex flex-wrap gap-2 mt-auto">
                {canManage && (
                  <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer border border-border rounded px-2.5 py-1.5 hover:bg-muted" data-testid={`permit-upload-${p.id}`}>
                    <Upload size={12} /> New version
                    <input type="file" hidden onChange={(e) => e.target.files && uploadVersion(p.id, e.target.files[0], "")} />
                  </label>
                )}
                <Button size="sm" variant="outline" className="border-border" onClick={() => setVersionsOf(p)} data-testid={`permit-history-${p.id}`}>
                  <ClockClockwise size={12} className="mr-1" /> {p.versions?.length || 0} versions
                </Button>
                {canManage && (
                  <Button size="sm" variant="outline" className="border-border" onClick={() => { setEditing(p); setForm({ ...EMPTY, ...p }); setOpen(true); }} data-testid={`permit-edit-${p.id}`}>
                    <PencilSimple size={12} />
                  </Button>
                )}
                <ExportMenu endpointPath={`/exports/permit/${p.id}`} baseName={`permit_${p.title.replace(/\s+/g,'_')}`} testId={`export-permit-${p.id}`} />
                {user?.role === "admin" && !p.deleted_at && (
                  <ArchiveButton endpoint={`/permits/${p.id}`} label={p.title} onArchived={load} testId={`archive-permit-${p.id}`} />
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle className="font-heading font-black">{editing ? "Edit Permit" : "New Permit"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5 col-span-2"><Label className="text-xs uppercase tracking-widest text-muted-foreground">Title</Label><Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="border-border" data-testid="permit-title-input" /></div>
              <div className="space-y-1.5"><Label className="text-xs uppercase tracking-widest text-muted-foreground">Type</Label><Input required value={form.permit_type} onChange={(e) => setForm({ ...form, permit_type: e.target.value })} className="border-border" data-testid="permit-type-input" /></div>
              <div className="space-y-1.5"><Label className="text-xs uppercase tracking-widest text-muted-foreground">Issuer</Label><Input value={form.issuer} onChange={(e) => setForm({ ...form, issuer: e.target.value })} className="border-border" /></div>
              <div className="space-y-1.5"><Label className="text-xs uppercase tracking-widest text-muted-foreground">Reference No.</Label><Input value={form.reference_no} onChange={(e) => setForm({ ...form, reference_no: e.target.value })} className="border-border" /></div>
              <div className="space-y-1.5"><Label className="text-xs uppercase tracking-widest text-muted-foreground">Issue Date</Label><Input type="date" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} className="border-border" /></div>
              <div className="space-y-1.5 col-span-2"><Label className="text-xs uppercase tracking-widest text-muted-foreground">Expiry Date</Label><Input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} className="border-border" /></div>
              <div className="space-y-1.5 col-span-2"><Label className="text-xs uppercase tracking-widest text-muted-foreground">Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="border-border" /></div>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="permit-submit-btn">{editing ? "Save" : "Create"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Versions dialog */}
      <Dialog open={!!versionsOf} onOpenChange={(v) => !v && setVersionsOf(null)}>
        <DialogContent className="max-w-xl" data-testid="permit-versions-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-black flex items-center gap-2"><ShieldCheck size={18} /> Versions — {versionsOf?.title}</DialogTitle>
          </DialogHeader>
          <div className="max-h-[400px] overflow-y-auto space-y-2">
            {(versionsOf?.versions || []).slice().reverse().map((v) => (
              <div key={v.version} className="border border-border rounded-md p-3 flex items-center justify-between">
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground">Version {v.version}</div>
                  <div className="font-medium text-sm">{v.file_name}</div>
                  <div className="text-xs text-muted-foreground">by {v.uploaded_by_name} · {(v.uploaded_at || "").slice(0, 10)}</div>
                </div>
                <a href={`${process.env.REACT_APP_BACKEND_URL}${v.file_url}${v.file_url.startsWith("/api/files/") ? `?auth=${encodeURIComponent(localStorage.getItem("access_token") || "")}` : ""}`} target="_blank" rel="noreferrer" className="text-xs border border-border rounded px-2.5 py-1.5 hover:bg-muted inline-flex items-center gap-1">
                  <DownloadSimple size={12} /> Download
                </a>
              </div>
            ))}
            {!versionsOf?.versions?.length && <div className="text-sm text-muted-foreground text-center py-8">No versions uploaded yet.</div>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
